import { useEffect, useState } from "react";
import { api, apiUrl, fmtINR, fmtCompact, fmtPct, fmtPeriod, periodOptions } from "@/lib/api";
import { PageHeader, Btn, Section, Badge, Select } from "@/components/Shared";
import { Download } from "lucide-react";

export default function Calculations() {
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("");
  const load = async () => {
    const { data } = await api.get(`/calculations${period ? `?period=${period}` : ""}`);
    setRows(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [period]);
  const periods = [...new Set(rows.map((r) => r.period))].sort();
  const totals = rows.reduce((a, r) => ({ gross: a.gross + (r.gross_commission || 0), payout: a.payout + (r.partner_payout || 0), net: a.net + (r.net_commission || 0), vol: a.vol + (r.processed_volume || 0) }), { gross: 0, payout: 0, net: 0, vol: 0 });

  return (
    <div data-testid="calculations-page">
      <PageHeader
        title="Commission Engine"
        actions={<>
          <div className="w-44"><Select placeholder="All periods" options={periodOptions(periods)} value={period} onChange={setPeriod} testid="calc-period" /></div>
          <Btn variant="ghost" testid="calc-export" onClick={() => window.open(apiUrl("/export/calculations"), "_blank")}><Download size={14} /> CSV</Btn>
        </>}
      />
      <div className="p-4 sm:p-10 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-[var(--surface)] border border-[var(--line)] p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total Revenue</div><div className="num text-2xl font-semibold mt-2">₹ {fmtCompact(totals.gross)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Partner's Payout</div><div className="num text-2xl font-semibold mt-2 text-[var(--accent)]">₹ {fmtCompact(totals.payout)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">BizAssist's Net Revenue</div><div className="num text-2xl font-semibold mt-2">₹ {fmtCompact(totals.net)}</div></div>
          <div className="bg-[var(--surface)] border border-[var(--line)] p-5"><div className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] font-semibold">Total Volume</div><div className="num text-2xl font-semibold mt-2">₹ {fmtCompact(totals.vol)}</div></div>
        </div>

        <Section title={`${rows.length} calculated rows`} testid="calc-section">
          <div className="overflow-auto max-h-[640px]">
            <div className="overflow-x-auto"><table className="table-data w-full">
              <thead><tr>
                <th>Period</th><th>Merchant ID</th><th>Merchant</th><th>GW</th><th>LOB</th>
                <th className="text-right">Volume</th>
                <th className="text-right">Base</th><th className="text-right">Billing</th>
                <th className="text-right">Base Cost</th><th className="text-right">Merch Billing</th>
                <th className="text-right">Adj</th>
                <th className="text-right">Revenue</th><th>Partner</th>
                <th className="text-right">Share</th><th className="text-right">Payout</th><th className="text-right">Net Revenue</th>
                <th>Status</th>
              </tr></thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={`${r.program_id}-${r.period}-${i}`} data-testid={`calc-row-${r.program_id}-${r.period}`}>
                    <td className="num font-medium">{fmtPeriod(r.period)}</td>
                    <td className="num">{r.merchant_id}</td>
                    <td className="font-medium">{r.merchant_name}</td>
                    <td>{r.gateway}</td>
                    <td className="text-[12px]">{r.lob}</td>
                    <td className="text-right num">₹ {fmtINR(r.processed_volume, 0)}</td>
                    <td className="text-right num text-[12px] text-[var(--muted)]">{fmtPct(r.base_rate, 2)}</td>
                    <td className="text-right num text-[12px] text-[var(--muted)]">{fmtPct(r.merchant_rate, 2)}</td>
                    <td className="text-right num">{fmtINR(r.base_earnings, 0)}</td>
                    <td className="text-right num">{fmtINR(r.merchant_billing, 0)}</td>
                    <td className={`text-right num ${r.manual_adjustments < 0 ? "text-[var(--negative)]" : ""}`}>{r.manual_adjustments ? fmtINR(r.manual_adjustments, 0) : "—"}</td>
                    <td className="text-right num font-semibold">{fmtINR(r.gross_commission, 0)}</td>
                    <td className="text-[12px]">{r.referred_partner_name}</td>
                    <td className="text-right num">{fmtPct(r.partner_share_pct)}</td>
                    <td className="text-right num text-[var(--accent)]">{fmtINR(r.partner_payout, 0)}</td>
                    <td className="text-right num text-[var(--positive)] font-medium">{fmtINR(r.net_commission, 0)}</td>
                    <td>{r.error ? <Badge tone="warning">Error</Badge> : (r.processed_volume > 0 ? <Badge tone="positive">OK</Badge> : <Badge tone="muted">No Vol</Badge>)}</td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </Section>
      </div>
    </div>
  );
}
